import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { validatedApi } from '../../../lib/api/validated-client';
import type {
  VenueResponse,
  VenueFilterQuery,
  CreateVenueRequest,
  UpdateVenueRequest,
  VenueApprovalRequest,
  PaginatedVenuesResponse,
} from '@lov/shared';
import type { FilterState } from './useVenueFilters';

// Re-export types for convenience
export type { VenueResponse };

// Transform FilterState to API-compatible VenueFilterQuery
function transformFiltersForApi(filters: FilterState): Partial<VenueFilterQuery> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { amenities, minRating, sortBy, ...rest } = filters;

  const apiParams: Partial<VenueFilterQuery> = { ...rest };

  // Convert amenities from Record<string, boolean> to comma-separated string
  if (amenities && typeof amenities === 'object') {
    const activeAmenities = Object.entries(amenities)
      .filter(([, value]) => value)
      .map(([key]) => key);
    if (activeAmenities.length > 0) {
      apiParams.amenities = activeAmenities.join(',');
    }
  }

  return apiParams;
}

// List venues (public)
export function useVenues(params: FilterState = {}) {
  const apiParams = transformFiltersForApi(params);

  return useQuery<PaginatedVenuesResponse>({
    queryKey: ['venues', params],
    queryFn: async () => {
      return validatedApi.get('/venues', { params: apiParams });
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Get single venue
export function useVenue(idOrSlug: string) {
  return useQuery<VenueResponse>({
    queryKey: ['venues', idOrSlug],
    queryFn: async () => {
      return validatedApi.get(`/venues/${idOrSlug}`);
      
    },
    enabled: !!idOrSlug,
  });
}

// Get venue by SEO path
export function useVenueByPath(stateSlug: string, citySlug: string, venueSlug: string) {
  return useQuery<VenueResponse>({
    queryKey: ['venues', 'path', stateSlug, citySlug, venueSlug],
    queryFn: async () => {
      return validatedApi.get(`/venues/${stateSlug}/${citySlug}/${venueSlug}`);
      
    },
    enabled: !!(stateSlug && citySlug && venueSlug),
  });
}

// Get vendor's venues
export function useMyVenues() {
  return useQuery<PaginatedVenuesResponse, Error, VenueResponse[]>({
    queryKey: ['venues', 'my'],
    queryFn: async () => {
      return validatedApi.get('/venues/my');
    },
    select: (response) => response.data,
  });
}

// Alias for useMyVenues (used by staff management)
export const useVendorVenues = useMyVenues;

// Get pending venues (admin)
export function usePendingVenues(page = 1, limit = 20) {
  return useQuery<PaginatedVenuesResponse>({
    queryKey: ['venues', 'pending', page, limit],
    queryFn: async () => {
      return validatedApi.get('/venues/pending', { params: { page, limit } });
      
    },
  });
}

// Create venue (admin)
export function useCreateVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (venueData: CreateVenueRequest) => {
      return validatedApi.post('/venues', venueData);
      
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
  });
}

// Update venue
export function useUpdateVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateVenueRequest }) => {
      return validatedApi.put(`/venues/${id}`, data);
      
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
      void queryClient.invalidateQueries({ queryKey: ['venues', variables.id] });
    },
  });
}

// Delete venue
export function useDeleteVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await validatedApi.delete(`/venues/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
  });
}

// Admin: Update venue (no ownership check)
export function useAdminUpdateVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateVenueRequest }) => {
      return validatedApi.put(`/venues/admin/${id}`, data);
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
      void queryClient.invalidateQueries({ queryKey: ['venues', variables.id] });
      void queryClient.invalidateQueries({ queryKey: ['venues', 'admin'] });
    },
  });
}

// Admin: Delete venue (no ownership check)
export function useAdminDeleteVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await validatedApi.delete(`/venues/admin/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
      void queryClient.invalidateQueries({ queryKey: ['venues', 'admin'] });
    },
  });
}

// Admin: List all venues (including unapproved/inactive)
export function useAdminVenues(params: FilterState = {}) {
  const apiParams = transformFiltersForApi(params);

  return useQuery<PaginatedVenuesResponse>({
    queryKey: ['venues', 'admin', params],
    queryFn: async () => {
      return validatedApi.get('/venues/admin', { params: apiParams });
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Approve venue (admin)
export function useApproveVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: VenueApprovalRequest }) => {
      return validatedApi.put(`/venues/${id}/approve`, data);
      
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
  });
}

// Upload images
export function useUploadVenueImages() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, files }: { id: string; files: File[] }) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('images', file));

      return validatedApi.post(`/venues/${id}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['venues', variables.id] });
    },
  });
}

// Delete image
export function useDeleteVenueImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, imageUrl }: { id: string; imageUrl: string }) => {
      await validatedApi.delete(`/venues/${id}/images`, { params: { url: imageUrl } });
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['venues', variables.id] });
    },
  });
}

// Set cover image
export function useSetCoverImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, imageUrl }: { id: string; imageUrl: string }) => {
      return validatedApi.put(`/venues/${id}/cover`, null, {
        params: { url: imageUrl },
      });

    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['venues', variables.id] });
    },
  });
}

// Admin: Upload venue images (no ownership check)
export function useAdminUploadVenueImages() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ venueId, files }: { venueId: string; files: File[] }) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('images', file));
      return validatedApi.post(`/venues/admin/${venueId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: (_, { venueId }) => {
      void queryClient.invalidateQueries({ queryKey: ['venues', venueId] });
      void queryClient.invalidateQueries({ queryKey: ['venues', 'admin'] });
    },
  });
}

// Admin: Delete venue image (no ownership check)
export function useAdminDeleteVenueImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ venueId, url }: { venueId: string; url: string }) => {
      await validatedApi.delete(`/venues/admin/${venueId}/images`, { params: { url } });
    },
    onSuccess: (_, { venueId }) => {
      void queryClient.invalidateQueries({ queryKey: ['venues', venueId] });
      void queryClient.invalidateQueries({ queryKey: ['venues', 'admin'] });
    },
  });
}

// Admin: Set venue cover image (no ownership check)
export function useAdminSetVenueCoverImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ venueId, url }: { venueId: string; url: string }) => {
      return validatedApi.put(`/venues/admin/${venueId}/cover`, null, { params: { url } });
    },
    onSuccess: (_, { venueId }) => {
      void queryClient.invalidateQueries({ queryKey: ['venues', venueId] });
      void queryClient.invalidateQueries({ queryKey: ['venues', 'admin'] });
    },
  });
}

// Admin: Reorder venue images (no ownership check)
export function useAdminReorderVenueImages() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ venueId, images }: { venueId: string; images: string[] }) => {
      return validatedApi.put(`/venues/admin/${venueId}/images/order`, { images });
    },
    onSuccess: (_, { venueId }) => {
      void queryClient.invalidateQueries({ queryKey: ['venues', venueId] });
      void queryClient.invalidateQueries({ queryKey: ['venues', 'admin'] });
    },
  });
}

// Change venue status (admin)
type VenueStatus = 'pending' | 'approved' | 'active' | 'rejected' | 'suspended';

interface ChangeVenueStatusInput {
  venueId: string;
  status: VenueStatus;
  reason?: string;
}

export function useChangeVenueStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ venueId, status, reason }: ChangeVenueStatusInput) => {
      // Use new state machine endpoint - clean and simple
      // Backend handles all status validation and mapping
      return validatedApi.put(`/venues/${venueId}/status`, { status, reason });
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
      void queryClient.invalidateQueries({ queryKey: ['venues', 'admin'] });
      void queryClient.invalidateQueries({ queryKey: ['venues', variables.venueId] });
    },
  });
}
